import styled, { css } from "styled-components";
import { colors, breakpoints } from "definitions";

/* ---------------------------------------------------------------------------
   Sistema de campos
   ---------------------------------------------------------------------------
   Antes cada control traía su propia caja: el input era sólo un borde inferior
   negro, el select un borde cyan completo y el textarea un borde gris, y
   ninguno mostraba foco ni error. Acá viven los tokens y el mixin `fieldBox`
   que comparten input, select, textarea y el link de sólo lectura, para que
   todos midan, se enfoquen y fallen igual.

   `mode="dark"` lo usan las pantallas de administración, que dibujan los
   formularios sobre un panel oscuro.
--------------------------------------------------------------------------- */

export const field = {
  height: "4.2rem",
  radius: "8px",
  light: {
    surface: colors.background.white,
    border: "#d5dde8",
    text: colors.fonts.darkGray,
    placeholder: "#9aa5b5",
    icon: "#7f8b9c",
  },
  dark: {
    surface: "rgba(0, 0, 0, 0.16)",
    border: "rgba(255, 255, 255, 0.45)",
    text: colors.fonts.white,
    placeholder: "rgba(255, 255, 255, 0.6)",
    icon: "rgba(255, 255, 255, 0.75)",
  },
  focus: {
    light: { border: colors.icons.yellowGreen, halo: "rgba(0, 110, 223, 0.16)" },
    dark: { border: colors.fonts.white, halo: "rgba(255, 255, 255, 0.22)" },
  },
  error: colors.background.red,
};

const palette = (mode) => (mode === "dark" ? field.dark : field.light);

const focusRing = (mode) =>
  mode === "dark" ? field.focus.dark : field.focus.light;

// Caja compartida por todos los controles de texto.
export const fieldBox = css`
  box-sizing: border-box;
  width: 100%;
  min-height: ${field.height};
  padding: 0 1.2rem;
  border: 1px solid
    ${({ mode, withError, witherror }) =>
      withError || witherror ? field.error : palette(mode).border};
  border-radius: ${field.radius};
  background: ${({ mode }) => palette(mode).surface};
  color: ${({ mode }) => palette(mode).text};
  font-family: Lato, sans-serif;
  font-size: 1.4rem;
  line-height: 1.4;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  ::placeholder {
    color: ${({ mode }) => palette(mode).placeholder};
  }

  :focus,
  :focus-visible {
    outline: none;
    border-color: ${({ mode }) => focusRing(mode).border};
    box-shadow: 0 0 0 3px ${({ mode }) => focusRing(mode).halo};
  }

  :disabled,
  :read-only {
    cursor: default;
  }
`;

/* Vista de sólo lectura (la postulación abierta desde el histórico): los
   campos dejan de dibujar la caja del input. Antes se veía igual que el
   formulario editable, con recuadros y flechita de select, y no se entendía
   que no se podía tocar nada. Queda como una ficha de datos: rótulo arriba,
   valor abajo, separados por una línea fina.

   Cuelga del estado nativo (`:read-only` en input y textarea, `:disabled` en
   select) en vez de una prop, porque el valor lo pone react-hook-form sobre el
   elemento y no llega por props. `readOnly` sólo lo usa este formulario, así
   que no alcanza a las pantallas de administración. */
const flatField = css`
  min-height: 3.2rem;
  padding: 0 0 0.4rem;
  border: none;
  border-bottom: 1px solid #dde3ee;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  color: ${({ mode }) =>
    mode === "dark" ? field.dark.text : colors.fonts.darkGray};
`;

export const Caption = styled.h3`
  font-weight: 500;
  font-size: 1.6rem;
  line-height: 2.5rem;
  color: ${(props) =>
    props.mode === "dark" ? `${colors.fonts.white}` : `${colors.fonts.black}`};
  padding-top: 1rem;
  width: 100%;

  margin-bottom: 5px;
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;

  select[multiple] {
    height: 12rem;

    option {
      padding: 6px 4px;
    }
  }
`;

export const FieldLabel = styled.label`
  font-weight: 600;
  font-size: 1.3rem;
  line-height: 1.8rem;
  color: ${(props) =>
    props.mode === "dark" ? `${colors.fonts.white}` : `${colors.fonts.darkGray}`};
  margin-bottom: 8px;
  width: 100%;

  :after {
    content: "${(props) => (props.required === true ? " *" : "")}";
    color: ${field.error};
  }
`;

export const ErrorMessage = styled.small`
  display: block;
  margin-top: 5px;
  font-size: 1.2rem;
  font-weight: 500;
  line-height: 1.5rem;
  color: ${(props) =>
    props.mode === "dark" ? `${colors.fonts.white}` : `${colors.fonts.red}`};
`;

export const FieldInput = styled.input`
  ${fieldBox};
  height: ${field.height};

  :read-only {
    ${flatField};
    height: auto;
  }
`;

// Cuando el campo lleva ícono o prefijo, la caja es el contenedor y el input
// de adentro queda sin borde: así el ícono vive dentro del mismo recuadro en
// vez de colgar afuera.
export const InputContainer = styled.div`
  ${fieldBox};
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0 1.2rem;
  overflow: hidden;

  :focus-within {
    border-color: ${({ mode }) => focusRing(mode).border};
    box-shadow: 0 0 0 3px ${({ mode }) => focusRing(mode).halo};
  }

  > svg {
    flex: 0 0 auto;
    width: 1.7rem;
    height: 1.7rem;
    margin: 0;
    color: ${({ mode }) => palette(mode).icon};
  }

  /* El segundo selector va a propósito: la regla de sólo lectura del propio
     input pesa más que un selector de elemento suelto, así que el input
     dibujaba su línea además de la del contenedor y quedaban dos. */
  input,
  input:read-only {
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  input:focus,
  input:focus-visible {
    border: none;
    box-shadow: none;
  }

  /* El contenedor es el que dibuja la caja cuando hay ícono, así que también
     se aplana cuando el campo es de sólo lectura. */
  :has(input:read-only) {
    ${flatField};
    gap: 0.6rem;
  }
`;

export const LinkContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 0;
  min-height: 3.2rem;
  padding: 0 0 0.4rem;
  border-bottom: 1px solid #dde3ee;
`;

export const LinkInput = styled.a`
  min-width: 0;
  font-weight: 400;
  font-size: 1.4rem;
  line-height: 2rem;
  color: ${colors.fonts.yellowGreen};
  text-decoration: underline;
  /* El recorte lo hace el ancho de la columna, no un substring fijo. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Campo opcional que quedó sin completar. Mejor una raya que un rótulo
// colgando sobre la nada.
export const EmptyValue = styled.span`
  /* Sin borde: la línea la dibuja LinkContainer, que lo envuelve. */
  font-size: 1.4rem;
  line-height: 2rem;
  color: #9aa5b5;
`;

const chevron = (stroke) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${stroke}' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`;

export const FieldSelect = styled.select`
  ${fieldBox};
  height: ${({ multiple }) => (multiple ? "auto" : field.height)};
  cursor: pointer;

  ${({ multiple, mode }) =>
    !multiple &&
    css`
      appearance: none;
      -webkit-appearance: none;
      padding-right: 3.6rem;
      background-image: ${chevron(
        mode === "dark" ? "%23ffffff" : "%23353535"
      )};
      background-repeat: no-repeat;
      background-position: right 1.2rem center;
      background-size: 1.6rem 1.6rem;
    `};

  /* Sólo lectura: sin caja ni flecha, se lee como el dato que es. */
  :disabled {
    ${flatField};
    height: auto;
    padding-right: 0;
    background-image: none;
    opacity: 1;
  }
`;

export const TextArea = styled.textarea`
  ${fieldBox};
  min-height: 11rem;
  padding: 1rem 1.2rem;
  resize: vertical;

  /* Las respuestas del cuestionario en la vista de lectura: texto corrido, sin
     recuadro y sin scroll interno. */
  :read-only {
    ${flatField};
    min-height: 0;
    padding: 0 0 0.8rem;
    resize: none;
    line-height: 2rem;
    /* Crece con la respuesta donde el browser lo soporta; donde no, scrollea
       en vez de cortar el texto. */
    overflow-y: auto;
    field-sizing: content;
  }
`;

export const Paragraph = styled.p`
  font-family: Lato;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  display: flex;
  align-items: center;
`;

/* ---------------------------------------------------------------------------
   Checkbox
   ---------------------------------------------------------------------------
   Antes el cuadrado se ubicaba con `left: 29px` sobre un `padding-left: 69px`
   y el texto quedaba limitado a `max-width: 80%`, así que la leyenda nunca
   alineaba con lo que tenía al lado. Ahora es una grilla de dos columnas: el
   cuadrado en la primera, el rótulo y la aclaración apilados en la segunda.
--------------------------------------------------------------------------- */

// Las opciones múltiples del cuestionario en dos columnas: en una sola quedan
// muy largas y en cinco (lo que había) el rótulo no entraba.
export const CheckContainerInput = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 2.4rem;
  width: 100%;

  /* El rótulo del grupo y su mensaje de error cruzan las dos columnas. */
  > label,
  > small {
    grid-column: 1 / -1;
  }

  ${breakpoints.tablet} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const CheckContainer = styled.label`
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: 1.2rem;
  row-gap: 0.2rem;
  align-items: start;
  width: 100%;
  margin: 0.6rem 0;
  padding: 0;
  font-family: Lato;
  font-size: 1.4rem;
  line-height: 2rem;
  font-weight: 300;
  cursor: ${({ readOnly }) => (readOnly ? "default" : "pointer")};
  user-select: none;

  color: ${({ mode }) =>
    mode === "dark" ? `${colors.fonts.white}` : `${colors.fonts.darkGray}`};

  > br {
    display: none;
  }

  > b,
  > label {
    grid-column: 2;
    font-weight: 600;
    cursor: inherit;
  }

  > div {
    grid-column: 2;
  }

  em {
    display: block;
    font-style: normal;
    font-size: 1.3rem;
    line-height: 1.8rem;
    opacity: 0.85;
  }

  a {
    font-weight: 600;
    color: ${({ mode }) =>
      mode === "dark" ? `${colors.fonts.white}` : `${colors.fonts.yellowGreen}`};
  }
`;

export const CheckInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  margin: 0;

  :checked ~ span {
    background-color: ${({ mode }) =>
      mode === "dark" ? colors.background.white : colors.icons.yellowGreen};
    border-color: ${({ mode }) =>
      mode === "dark" ? colors.background.white : colors.icons.yellowGreen};
  }

  :checked ~ span:after {
    display: block;
  }

  :focus-visible ~ span {
    box-shadow: 0 0 0 3px
      ${({ mode }) => (mode === "dark" ? field.focus.dark.halo : field.focus.light.halo)};
  }
`;

export const CheckSpan = styled.span`
  position: relative;
  grid-column: 1;
  grid-row: 1;
  flex: 0 0 auto;
  width: 2rem;
  height: 2rem;
  margin-top: 0.1rem;
  border: 2px solid
    ${({ mode }) =>
      mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "#9aa5b5"};
  border-radius: 5px;
  background-color: transparent;
  transition: background-color 0.12s linear, border-color 0.12s linear;

  :after {
    content: "";
    display: none;
    position: absolute;
    left: 0.55rem;
    top: 0.15rem;
    width: 0.5rem;
    height: 1rem;
    border: solid;
    border-color: ${({ mode }) =>
      mode === "dark" ? colors.background.gray : colors.background.white};
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
  }
`;

// Sólo existe para el modo de dos líneas del cuestionario. La grilla del
// checkbox ya arma esa disposición, así que no dibuja nada.
export const Break = styled.br`
  display: none;
`;

// Envuelve cada opción de un grupo (radios o checkboxes). No fija ancho: lo
// decide el contenedor, que en ponderación son pastillas al ancho del texto y
// en opciones múltiples una grilla de dos columnas.
export const Col = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

/* ---------------------------------------------------------------------------
   Ponderación (radios)
   ---------------------------------------------------------------------------
   Cinco columnas de texto centrado bajo un círculo eran difíciles de asociar
   con su opción. Cada opción pasa a ser una pastilla que se puede tocar
   entera, y en mobile se apilan sin romper el ancho.
--------------------------------------------------------------------------- */

export const RadioGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const RadioGroupOptions = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.8rem;
  width: 100%;

  li {
    display: flex;
    margin: 0;
    min-width: 0;
  }

  li input[type="radio"] {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }

  li label {
    display: inline-flex;
    align-items: center;
    gap: 0.9rem;
    padding: 0.7rem 1.4rem 0.7rem 1rem;
    border: 1px solid
      ${({ mode }) =>
        mode === "dark" ? "rgba(255, 255, 255, 0.45)" : field.light.border};
    border-radius: 999px;
    background: ${({ mode }) =>
      mode === "dark" ? "rgba(0, 0, 0, 0.16)" : colors.background.white};
    color: ${({ mode }) =>
      mode === "dark" ? colors.fonts.white : colors.fonts.darkGray};
    font-family: Lato;
    font-size: 1.4rem;
    font-weight: 400;
    line-height: 2rem;
    cursor: pointer;
    transition: border-color 0.12s linear, background-color 0.12s linear;
  }

  li:hover label {
    border-color: ${({ mode }) =>
      mode === "dark" ? colors.fonts.white : colors.icons.yellowGreen};
  }

  li label .check {
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    width: 1.8rem;
    height: 1.8rem;
    border: 2px solid
      ${({ mode }) =>
        mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "#9aa5b5"};
    border-radius: 50%;
    transition: border-color 0.12s linear;
  }

  li label .check::before {
    content: "";
    display: block;
    width: 0.9rem;
    height: 0.9rem;
    border-radius: 50%;
    background: transparent;
    transition: background 0.12s linear;
  }

  input[type="radio"]:checked ~ label {
    border-color: ${({ mode }) =>
      mode === "dark" ? colors.fonts.white : colors.icons.yellowGreen};
    background: ${({ mode }) =>
      mode === "dark" ? "rgba(255, 255, 255, 0.16)" : "#e8f1fd"};
    color: ${({ mode }) =>
      mode === "dark" ? colors.fonts.white : colors.fonts.yellowGreen};
    font-weight: 600;
  }

  input[type="radio"]:checked ~ label .check {
    border-color: ${({ mode }) =>
      mode === "dark" ? colors.fonts.white : colors.icons.yellowGreen};
  }

  input[type="radio"]:checked ~ label .check::before {
    background: ${({ mode }) =>
      mode === "dark" ? colors.fonts.white : colors.icons.yellowGreen};
  }

  input[type="radio"]:focus-visible ~ label {
    box-shadow: 0 0 0 3px
      ${({ mode }) => (mode === "dark" ? field.focus.dark.halo : field.focus.light.halo)};
  }

  ${breakpoints.phablet} {
    flex-direction: column;
    align-items: stretch;

    li label {
      width: 100%;
    }
  }
`;

export const FieldAutoCompleteGroup = styled(FieldGroup)`
  position: relative;
  z-index: 5;
`;

export const AutoCompleteOptionContainer = styled.div`
  position: absolute;
  top: 100%;
  width: 100%;
  margin-top: 4px;
  border: 1px solid ${field.light.border};
  border-radius: ${field.radius};
  background: ${({ mode }) =>
    mode === "dark" ? field.dark.surface : field.light.surface};
  box-shadow: 0 8px 24px rgba(8, 37, 90, 0.16);
  overflow: hidden;
  z-index: 10;
`;

export const AutoCompleteOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.2rem;
  font-size: 1.4rem;
  line-height: 2rem;
  text-transform: capitalize;
  color: ${colors.fonts.darkGray};
  cursor: pointer;

  :hover {
    background: #f5f8ff;
  }
`;

/* ---------------------------------------------------------------------------
   MultiSelectField: reemplaza al <select multiple> nativo (imposible de usar
   con muchas opciones: scroll de 3 filas, ctrl+click para sumar y sin forma de
   ver lo elegido). Muestra todas las opciones en columnas, agrupadas por
   sección. El <select> sigue en el DOM pero oculto, así react-hook-form lo
   registra igual que antes y el submit no cambia.
--------------------------------------------------------------------------- */

export const HiddenSelect = styled.select`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

export const MultiSelectPanel = styled.div`
  border: 1px solid
    ${({ witherror, mode }) =>
      witherror
        ? `${colors.background.red}`
        : mode === "dark"
        ? `rgba(255, 255, 255, 0.45)`
        : `${field.light.border}`};
  border-radius: ${field.radius};
  background: ${({ mode }) =>
    mode === "dark" ? `rgba(0, 0, 0, 0.14)` : `${colors.background.white}`};
  color: ${({ mode }) =>
    mode === "dark" ? `${colors.fonts.white}` : `${colors.fonts.darkGray}`};
  overflow: hidden;
`;

export const MultiSelectToolbar = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1.6rem;
  padding: 1.2rem 1.6rem;
  border-bottom: 1px solid
    ${({ mode }) =>
      mode === "dark" ? `rgba(255, 255, 255, 0.2)` : `#e5e5e5`};
`;

export const MultiSelectCounter = styled.span`
  font-family: Lato;
  font-size: 1.3rem;
  font-weight: 600;
  opacity: 0.85;
`;

export const MultiSelectAction = styled.button`
  flex: 0 0 auto;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font-family: Lato;
  font-size: 1.3rem;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;

  :hover:enabled {
    opacity: 0.75;
  }

  :disabled {
    opacity: 0.35;
    cursor: default;
    text-decoration: none;
  }
`;

export const MultiSelectGroup = styled.div`
  padding: 1.2rem 1.6rem 1.6rem;

  + div {
    border-top: 1px solid
      ${({ mode }) =>
        mode === "dark" ? `rgba(255, 255, 255, 0.14)` : `#eeeeee`};
  }
`;

export const MultiSelectGroupTitle = styled.h4`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1.6rem;
  margin: 0 0 1rem;
  font-family: Lato;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.08rem;
  text-transform: uppercase;
  color: ${({ mode }) => (mode === "dark" ? `rgba(255,255,255,0.7)` : `#777`)};
`;

export const MultiSelectGroupCount = styled.span`
  flex: 0 0 auto;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0;
  opacity: 0.7;
`;

export const MultiSelectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns || 3}, minmax(0, 1fr));
  gap: 0.4rem 1.6rem;

  ${breakpoints.laptop} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${breakpoints.tablet} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const MultiSelectOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 1.2rem;
  padding: 0.8rem 1rem;
  border-radius: 6px;
  font-family: Lato;
  font-size: 1.4rem;
  line-height: 2rem;
  cursor: pointer;
  user-select: none;

  background: ${({ checked, mode }) =>
    checked
      ? mode === "dark"
        ? `rgba(255, 255, 255, 0.16)`
        : `#eef4ff`
      : `transparent`};

  font-weight: ${({ checked }) => (checked ? 600 : 300)};

  transition: background-color 0.12s linear;

  :hover {
    background: ${({ mode }) =>
      mode === "dark" ? `rgba(255, 255, 255, 0.26)` : `#f5f8ff`};
  }

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  input:focus-visible ~ span:first-of-type {
    box-shadow: 0 0 0 3px
      ${({ mode }) =>
        mode === "dark" ? `rgba(255, 255, 255, 0.5)` : `rgba(0, 110, 223, 0.4)`};
  }
`;

export const MultiSelectBox = styled.span`
  position: relative;
  flex: 0 0 auto;
  width: 2rem;
  height: 2rem;
  border-radius: 5px;
  border: 2px solid
    ${({ mode }) => (mode === "dark" ? `#ffffff` : `${colors.icons.gray}`)};

  background: ${({ checked, mode }) =>
    checked
      ? mode === "dark"
        ? `#ffffff`
        : `${colors.icons.gray}`
      : `transparent`};

  :after {
    content: "";
    display: ${({ checked }) => (checked ? "block" : "none")};
    position: absolute;
    left: 0.55rem;
    top: 0.15rem;
    width: 0.5rem;
    height: 1rem;
    border: solid;
    border-color: ${({ mode }) => (mode === "dark" ? `#451cb8` : `#ffffff`)};
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
  }
`;

export const MultiSelectOptionLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

export const MultiSelectEmpty = styled.p`
  margin: 0;
  padding: 2rem 1.6rem;
  font-family: Lato;
  font-size: 1.4rem;
  font-weight: 300;
  text-align: center;
  opacity: 0.8;
`;
